import { lazy, useCallback } from 'react';

import {
  CalendarConnectionStatusDtoStatus,
  NylasGrantDtoProvider,
  UnifiedConnectionDtoProvider,
  useIsCalendarConnected
} from '@/api/generated';
import useLoginContext from '@/context/LoginFlowContextProvider/useLoginContext';
import useConnections from '@/hooks/Integrations/useConnections';
import useIntegrations from '@/hooks/Integrations/useIntegrations';
import useCalendarConnectionStatus from '@/hooks/useCalendarConnectionStatus';
import useModal from '@/hooks/useModal/useModal';
import { integrationsAuth } from '@/mappings/integrationsAuth';
import {
  buildConnectionUrl,
  UnifiedIntegrationProvider,
  useUnifiedIntegrationsAuth
} from '@/mappings/unifiedIntegrationsAuth';
import useMessageListener from '@/utils/useMessageListener';

const LazyAreYouSureModal = lazy(
  () => import('@/components/modals/AreYouSureModal/AreYouSureModal')
);

export const useIntegrationsLogic = () => {
  const { userGrant, refetchUserGrant, deleteGoogleAuthentication, deleteMicrosoftAuthentication } =
    useIntegrations();
  const { conntectionList, refetchConnections, deleteConnection } = useConnections();
  const { showModal } = useModal();
  const { currentUser } = useLoginContext();
  const unifiedIntegrationsAuth = useUnifiedIntegrationsAuth();
  const listen = useMessageListener();
  const { refetch: refetchCalendarConnectionStatus } = useIsCalendarConnected({
    query: { enabled: false }
  });
  const calendarConnectionStatus = useCalendarConnectionStatus();
  const hasGoogle = userGrant ? userGrant.provider === NylasGrantDtoProvider.google : false;
  const hasMicrosoft = userGrant ? userGrant.provider === NylasGrantDtoProvider.microsoft : false;

  const disconnectGoogle = useCallback(() => {
    deleteGoogleAuthentication().then(() => {
      refetchUserGrant();
    });
  }, [deleteGoogleAuthentication, refetchUserGrant]);

  const disconnectMicrosoft = useCallback(() => {
    deleteMicrosoftAuthentication().then(() => {
      refetchUserGrant();
    });
  }, [deleteMicrosoftAuthentication, refetchUserGrant]);

  const disconnectUnified = useCallback(
    (provider: UnifiedIntegrationProvider | string) => {
      const providerType = typeof provider === 'string' ? provider : provider.type;
      const connectionId = conntectionList?.find(
        conn => conn.provider?.toLowerCase() === providerType.toLowerCase()
      )?.connectionId;
      if (connectionId) {
        deleteConnection(connectionId).then(() => {
          refetchConnections();
        });
      }
    },
    [conntectionList, deleteConnection, refetchConnections]
  );

  const onDisconnect = useCallback(
    (provider: NylasGrantDtoProvider | UnifiedIntegrationProvider | string) => {
      const modal = showModal(LazyAreYouSureModal, {
        onClose: () => {
          modal.hide();
        },
        onSubmit: () => {
          if (provider === NylasGrantDtoProvider.google) {
            disconnectGoogle();
          } else if (provider === NylasGrantDtoProvider.microsoft) {
            disconnectMicrosoft();
          } else {
            disconnectUnified(provider);
          }
          modal.hide();
        },
        title: `Disconnect ${provider === NylasGrantDtoProvider.google ? 'Google' : provider === NylasGrantDtoProvider.microsoft ? 'Microsoft' : typeof provider === 'string' ? provider : provider?.name}`,
        actionTitle: 'Disconnect',
        description: `Are you sure you want to disconnect ${provider === NylasGrantDtoProvider.google ? 'Google' : provider === NylasGrantDtoProvider.microsoft ? 'Microsoft' : typeof provider === 'string' ? provider : provider?.name}?`
      });
    },
    [showModal, disconnectGoogle, disconnectMicrosoft, disconnectUnified]
  );

  const connectToGoogle = useCallback(() => {
    if (!hasMicrosoft) {
      // Set up the listener BEFORE opening the popup
      const cleanup = listen(() => {
        refetchUserGrant();
        if (calendarConnectionStatus === CalendarConnectionStatusDtoStatus.DISCONNECTED) {
          refetchCalendarConnectionStatus();
        }
        return true;
      }, window.origin);

      // Then open the popup
      const popup = window.open(
        integrationsAuth.google.connectionUrl(),
        'authPopup',
        'width=500,height=700'
      );

      // Clean up listener if popup fails to open
      if (!popup) {
        cleanup();
      }
    }
  }, [
    hasMicrosoft,
    refetchUserGrant,
    listen,
    calendarConnectionStatus,
    refetchCalendarConnectionStatus
  ]);

  const connectToMicrosoft = useCallback(() => {
    if (!hasGoogle) {
      const cleanup = listen(() => {
        refetchUserGrant();
        if (calendarConnectionStatus === CalendarConnectionStatusDtoStatus.DISCONNECTED) {
          refetchCalendarConnectionStatus();
        }
        return true;
      }, window.origin);

      const popup = window.open(
        integrationsAuth.microsoft.connectionUrl(),
        'authPopup',
        'width=500,height=700'
      );

      if (!popup) {
        cleanup();
      }
    }
  }, [
    hasGoogle,
    refetchUserGrant,
    listen,
    calendarConnectionStatus,
    refetchCalendarConnectionStatus
  ]);

  const connectToUnifiedProvider = useCallback(
    (provider: UnifiedIntegrationProvider) => {
      const url = buildConnectionUrl(
        provider.type as UnifiedConnectionDtoProvider,
        provider.redirectUrl!,
        provider.scopes!,
        currentUser?.id
      );

      const cleanup = listen(() => {
        refetchConnections();
        return true;
      }, window.origin);

      const popup = window.open(url, 'authPopup', 'width=500,height=700');

      if (!popup) {
        cleanup();
      }
    },
    [currentUser?.id, listen, refetchConnections]
  );

  const isConnected = useCallback(
    (provider: UnifiedConnectionDtoProvider | string) =>
      conntectionList?.find(conn => conn.provider?.toLowerCase() === provider.toLowerCase()) !==
      undefined,
    [conntectionList]
  );

  return {
    userGrant,
    conntectionList,
    hasGoogle,
    hasMicrosoft,
    onDisconnect,
    connectToGoogle,
    connectToMicrosoft,
    connectToUnifiedProvider,
    isConnected,
    unifiedIntegrationsAuth
  };
};
